/**
 * Bug #3 Fix: Mutex Lock Composable for preventing race conditions
 *
 * This composable provides a robust mutex locking mechanism to prevent
 * concurrent operations from interfering with each other.
 *
 * Features:
 * - Queue-based lock management
 * - Timeout protection
 * - Automatic cleanup
 * - Multiple named locks support
 *
 * Bug #31 Fix: Added forceReleaseLocks to prevent deadlocks on component unmount
 */

// Global lock registry to track all active locks
const locks = new Map();
const lockQueues = new Map();
// Track active timeout IDs for cleanup
const lockTimeouts = new Map();

export function useMutexLock() {
  /**
   * Release a lock and process queue
   * @param {string} lockName - Lock identifier to release
   */
  const releaseLock = lockName => {
    locks.set(lockName, false);

    // Clear timeout if exists
    if (lockTimeouts.has(lockName)) {
      clearTimeout(lockTimeouts.get(lockName));
      lockTimeouts.delete(lockName);
    }

    // Process queue if exists
    if (lockQueues.has(lockName)) {
      const queue = lockQueues.get(lockName);
      if (queue.length > 0) {
        const next = queue.shift();
        // Schedule next acquisition in next tick to avoid recursion issues
        setTimeout(next, 0);
      }
    }
  };

  /**
   * Acquire a lock with the given name
   * @param {string} lockName - Unique identifier for the lock
   * @param {number} timeout - Maximum time to wait for lock in ms (default: 30000)
   * @returns {Promise<Function>} - Returns a release function
   */
  const acquireLock = async (lockName, timeout = 30000) => {
    return new Promise(resolve => {
      const attemptAcquire = () => {
        if (!locks.has(lockName) || locks.get(lockName) === false) {
          // Lock is available
          locks.set(lockName, true);

          // Setup timeout protection
          const timeoutId = setTimeout(() => {
            releaseLock(lockName);
          }, timeout);

          // Store timeout ID for cleanup
          lockTimeouts.set(lockName, timeoutId);

          // Return release function
          const release = () => {
            clearTimeout(timeoutId);
            lockTimeouts.delete(lockName);
            releaseLock(lockName);
          };

          resolve(release);
        } else {
          // Lock is held - add to queue
          if (!lockQueues.has(lockName)) {
            lockQueues.set(lockName, []);
          }
          lockQueues.get(lockName).push(attemptAcquire);
        }
      };

      attemptAcquire();
    });
  };

  /**
   * Check if a lock is currently held
   * @param {string} lockName - Lock identifier to check
   * @returns {boolean}
   */
  const isLocked = lockName => {
    return locks.get(lockName) === true;
  };

  /**
   * Force release all locks (use with caution - mainly for cleanup)
   */
  const releaseAllLocks = () => {
    // Clear all timeouts first
    lockTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    lockTimeouts.clear();
    locks.clear();
    lockQueues.clear();
  };

  /**
   * Bug #31 Fix: Force release specific locks by name pattern
   * Use this in component onBeforeUnmount to prevent deadlocks
   * @param {string[]} lockNames - Array of lock names to release
   */
  const forceReleaseLocks = lockNames => {
    lockNames.forEach(lockName => {
      // Clear timeout if exists
      if (lockTimeouts.has(lockName)) {
        clearTimeout(lockTimeouts.get(lockName));
        lockTimeouts.delete(lockName);
      }
      // Force release the lock
      locks.set(lockName, false);
      // Clear the queue for this lock to prevent stale callbacks
      if (lockQueues.has(lockName)) {
        lockQueues.set(lockName, []);
      }
    });
  };

  /**
   * Get queue size for a specific lock (for debugging)
   * @param {string} lockName - Lock identifier
   * @returns {number}
   */
  const getQueueSize = lockName => {
    return lockQueues.get(lockName)?.length || 0;
  };

  return {
    acquireLock,
    releaseLock,
    isLocked,
    releaseAllLocks,
    forceReleaseLocks,
    getQueueSize,
  };
}
